/**
 * Mortgage-evidence review, execution-status, and fact-batch actions.
 */
import { formatInteger } from "./format.js";
import { labelize } from "./dashboard-html.js";
import { authenticatedFetch, DashboardAuthRequiredError } from "./dashboard-auth.js";
import type { DashboardActionHost } from "./dashboard-referenced-deed-actions.js";
import { leadFactoryManualEvidenceStatusUrl, leadFactoryMortgageEvidenceReviewUrl } from "./dashboard-urls.js";

export function bindMortgageEvidenceReviewActionForms(statusElement: HTMLElement, actions: DashboardActionHost): void {
  document.querySelectorAll<HTMLFormElement>("[data-mortgage-evidence-action-form]").forEach((form) => {
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      const submitter = event.submitter instanceof HTMLButtonElement ? event.submitter : null;
      const action = submitter?.value ?? "";
      void submitMortgageEvidenceReviewAction(form, action, statusElement, actions);
    });
  });
  document.querySelectorAll<HTMLFormElement>("[data-mortgage-evidence-execution-status-form]").forEach((form) => {
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      void submitMortgageEvidenceExecutionStatus(form, statusElement, actions);
    });
  });
  document.querySelectorAll<HTMLFormElement>("[data-mortgage-evidence-execution-status-batch-form]").forEach((form) => {
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      void submitMortgageEvidenceExecutionStatusBatch(form, statusElement, actions);
    });
  });
  document.querySelectorAll<HTMLFormElement>("[data-mortgage-evidence-fact-batch-form]").forEach((form) => {
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      void submitMortgageEvidenceFactBatch(form, statusElement, actions);
    });
  });
}

export async function submitMortgageEvidenceReviewAction(form: HTMLFormElement, action: string, statusElement: HTMLElement, actions: DashboardActionHost): Promise<void> {
  const leadId = form.dataset.leadId;
  const sourceDocumentId = form.dataset.sourceDocumentId;
  const reviewState = form.dataset.reviewState;
  const assignedTo = form.querySelector<HTMLInputElement>("[data-mortgage-evidence-assignee]")?.value.trim() ?? "";
  if (!leadId) {
    statusElement.textContent = "Mortgage evidence action cancelled; lead id is missing.";
    return;
  }
  if (!action) {
    statusElement.textContent = "Mortgage evidence action cancelled; choose an action.";
    return;
  }
  if (action === "assign" && !assignedTo) {
    statusElement.textContent = "Mortgage evidence assignment cancelled; no assignee entered.";
    return;
  }
  statusElement.textContent = `${leadId}: applying mortgage evidence ${labelize(action)}...`;
  try {
    const response = await authenticatedFetch("/dashboard/lead-factory/mortgage-evidence-review/action", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        action,
        leadIds: [leadId],
        sourceDocumentIds: sourceDocumentId ? [sourceDocumentId] : [],
        reviewState,
        assignedTo: assignedTo || undefined,
        limit: 1,
        note: "Mortgage Evidence Review queue action from dashboard."
      })
    });
    const body = (await response.json()) as { error?: string; actedCount?: number; skippedCount?: number };
    if (!response.ok) throw new Error(body.error ?? `Request failed with ${response.status}`);
    statusElement.textContent = `${leadId}: mortgage evidence ${labelize(action)} applied to ${formatInteger(body.actedCount ?? 0)} row${(body.actedCount ?? 0) === 1 ? "" : "s"}; skipped ${formatInteger(body.skippedCount ?? 0)}. Refreshing...`;
    window.setTimeout(() => actions.refresh(), 900);
  } catch (error) {
    if (error instanceof DashboardAuthRequiredError) {
      actions.onAuthRequired(error.message);
      return;
    }
    statusElement.textContent = error instanceof Error ? error.message : "Mortgage evidence action failed.";
  }
}

export async function submitMortgageEvidenceExecutionStatus(form: HTMLFormElement, statusElement: HTMLElement, actions: DashboardActionHost): Promise<void> {
  const taskId = form.dataset.taskId || "";
  const leadId = form.dataset.leadId || taskId;
  const acquisitionStatus = form.querySelector<HTMLSelectElement>("[data-mortgage-evidence-execution-status]")?.value.trim() || "";
  if (!taskId) {
    statusElement.textContent = "Mortgage evidence execution status cancelled; task id is missing.";
    return;
  }
  if (!acquisitionStatus) {
    statusElement.textContent = `${leadId}: choose a mortgage evidence execution status.`;
    return;
  }
  const sourceUrl = form.querySelector<HTMLInputElement>("[data-mortgage-evidence-execution-source-url]")?.value.trim() || "";
  const nextDueAt = form.querySelector<HTMLInputElement>("[data-mortgage-evidence-execution-next-due-at]")?.value.trim() || "";
  const terminalDisposition = Boolean(form.querySelector<HTMLInputElement>("[data-mortgage-evidence-terminal-disposition]")?.checked);
  const terminalReason = form.querySelector<HTMLSelectElement>("[data-mortgage-evidence-terminal-reason]")?.value.trim() || "";
  if (terminalDisposition && (!terminalReason || acquisitionStatus !== "source_unavailable")) {
    statusElement.textContent = `${leadId}: choose Source unavailable plus a terminal reason before closing mortgage evidence.`;
    return;
  }
  const note =
    form.querySelector<HTMLInputElement>("[data-mortgage-evidence-execution-note]")?.value.trim() ||
    (terminalDisposition
      ? "Mortgage evidence terminal no-source disposition recorded from Mortgage Evidence Review."
      : "Mortgage evidence execution status recorded from Mortgage Evidence Review.");
  statusElement.textContent = `${leadId}: recording mortgage evidence ${labelize(acquisitionStatus)}...`;
  try {
    const response = await authenticatedFetch(leadFactoryManualEvidenceStatusUrl(taskId), {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        acquisitionStatus,
        sourceUrl: sourceUrl || undefined,
        nextDueAt: nextDueAt || undefined,
        terminalDisposition,
        terminalReason: terminalReason || undefined,
        note
      })
    });
    const body = (await response.json()) as {
      error?: string;
      acquisitionStatus?: string;
      status?: string;
      taskId?: string;
      terminalDisposition?: { status?: string; terminalReason?: string; completedTaskIds?: string[] } | null;
    };
    if (!response.ok) throw new Error(body.error ?? `Request failed with ${response.status}`);
    const terminalText = body.terminalDisposition
      ? ` terminal ${labelize(body.terminalDisposition.terminalReason ?? terminalReason)}; completed ${formatInteger(body.terminalDisposition.completedTaskIds?.length ?? 0)} tasks.`
      : ` task remains ${labelize(body.status ?? "open")}.`;
    statusElement.textContent = `${leadId}: recorded ${labelize(body.acquisitionStatus ?? acquisitionStatus)};${terminalText} Refreshing...`;
    window.setTimeout(() => actions.refresh(), 900);
  } catch (error) {
    if (error instanceof DashboardAuthRequiredError) {
      actions.onAuthRequired(error.message);
      return;
    }
    statusElement.textContent = error instanceof Error ? error.message : "Mortgage evidence execution status failed.";
  }
}

export async function submitMortgageEvidenceExecutionStatusBatch(form: HTMLFormElement, statusElement: HTMLElement, actions: DashboardActionHost): Promise<void> {
  const taskIds = (form.dataset.taskIds || "").split(",").map((taskId) => taskId.trim()).filter(Boolean);
  const acquisitionStatus = form.querySelector<HTMLSelectElement>("[data-mortgage-evidence-execution-status]")?.value.trim() || "";
  const terminalDisposition = Boolean(form.querySelector<HTMLInputElement>("[data-mortgage-evidence-terminal-disposition]")?.checked);
  const terminalReason = form.querySelector<HTMLSelectElement>("[data-mortgage-evidence-terminal-reason]")?.value.trim() || "";
  if (taskIds.length === 0) {
    statusElement.textContent = "Mortgage evidence batch status cancelled; no visible routed tasks need status.";
    return;
  }
  if (!acquisitionStatus) {
    statusElement.textContent = "Choose a mortgage evidence execution status before recording the batch.";
    return;
  }
  if (terminalDisposition && (!terminalReason || acquisitionStatus !== "source_unavailable")) {
    statusElement.textContent = "Choose Source unavailable plus a terminal reason before closing a mortgage evidence batch.";
    return;
  }
  const note =
    form.querySelector<HTMLInputElement>("[data-mortgage-evidence-execution-note]")?.value.trim() ||
    (terminalDisposition
      ? "Mortgage evidence terminal no-source batch disposition recorded from Mortgage Evidence Review."
      : "Mortgage evidence execution status batch recorded from Mortgage Evidence Review.");
  statusElement.textContent = `Recording ${formatInteger(taskIds.length)} mortgage evidence status${taskIds.length === 1 ? "" : "es"}...`;
  try {
    const response = await authenticatedFetch("/dashboard/lead-factory/mortgage-evidence-review/execution-status-batch", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        taskIds,
        acquisitionStatus,
        terminalDisposition,
        terminalReason: terminalReason || undefined,
        note,
        confirmation: terminalDisposition ? "record_terminal_mortgage_evidence_disposition" : undefined,
        onlyWithoutExecutionStatus: true
      })
    });
    const body = (await response.json()) as { error?: string; actedCount?: number; skippedCount?: number; errorCount?: number };
    if (!response.ok) throw new Error(body.error ?? `Request failed with ${response.status}`);
    statusElement.textContent = `Mortgage evidence batch recorded ${formatInteger(body.actedCount ?? 0)} task${(body.actedCount ?? 0) === 1 ? "" : "s"}; skipped ${formatInteger(body.skippedCount ?? 0)}; errors ${formatInteger(body.errorCount ?? 0)}. Refreshing...`;
    window.setTimeout(() => actions.refresh(), 900);
  } catch (error) {
    if (error instanceof DashboardAuthRequiredError) {
      actions.onAuthRequired(error.message);
      return;
    }
    statusElement.textContent = error instanceof Error ? error.message : "Mortgage evidence batch execution status failed.";
  }
}

export async function submitMortgageEvidenceFactBatch(form: HTMLFormElement, statusElement: HTMLElement, actions: DashboardActionHost): Promise<void> {
  const rawRows = form.querySelector<HTMLTextAreaElement>("[data-mortgage-evidence-fact-batch-rows]")?.value.trim() || "";
  if (!rawRows) {
    statusElement.textContent = "Mortgage evidence fact batch cancelled; paste verified source-backed rows first.";
    return;
  }
  const rows = parseMortgageEvidenceFactBatchRows(rawRows);
  if (rows.length === 0) {
    statusElement.textContent = "Mortgage evidence fact batch cancelled; no valid rows were parsed.";
    return;
  }
  const invalidRow = rows.find((row) => !row.sourceDocumentId || !row.mortgageAmount || !row.normalizedValue || !row.sourcePage || !row.evidenceExcerpt);
  if (invalidRow) {
    statusElement.textContent = "Mortgage evidence fact batch cancelled; every row needs source document, amount, normalized amount, page, and excerpt.";
    return;
  }
  statusElement.textContent = `Capturing ${formatInteger(rows.length)} source-backed mortgage fact${rows.length === 1 ? "" : "s"}...`;
  try {
    const response = await authenticatedFetch("/dashboard/lead-factory/mortgage-evidence-review/facts-batch", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        rows,
        confirmation: rows.length > 1 ? "record_source_backed_mortgage_facts" : undefined,
        note: "Captured source-backed mortgage amount facts from Mortgage Evidence Review batch."
      })
    });
    const body = (await response.json()) as { error?: string; capturedCount?: number; skippedCount?: number; errorCount?: number };
    if (!response.ok) throw new Error(body.error ?? `Request failed with ${response.status}`);
    statusElement.textContent = `Mortgage evidence facts captured ${formatInteger(body.capturedCount ?? 0)} row${(body.capturedCount ?? 0) === 1 ? "" : "s"}; skipped ${formatInteger(body.skippedCount ?? 0)}; errors ${formatInteger(body.errorCount ?? 0)}. Refreshing...`;
    window.setTimeout(() => actions.refresh(), 900);
  } catch (error) {
    if (error instanceof DashboardAuthRequiredError) {
      actions.onAuthRequired(error.message);
      return;
    }
    statusElement.textContent = error instanceof Error ? error.message : "Mortgage evidence fact batch failed.";
  }
}

export function parseMortgageEvidenceFactBatchRows(rawRows: string): Array<{
  sourceDocumentId: string;
  mortgageAmount: string;
  normalizedValue: string;
  sourcePage: string;
  evidenceExcerpt: string;
}> {
  try {
    const parsed = JSON.parse(rawRows) as unknown;
    if (Array.isArray(parsed)) {
      return parsed.map((row) => {
        const record = row as Record<string, unknown>;
        return {
          sourceDocumentId: String(record.sourceDocumentId ?? "").trim(),
          mortgageAmount: String(record.mortgageAmount ?? record.value ?? "").trim(),
          normalizedValue: String(record.normalizedValue ?? record.mortgageAmount ?? record.value ?? "").trim(),
          sourcePage: String(record.sourcePage ?? "").trim(),
          evidenceExcerpt: String(record.evidenceExcerpt ?? "").trim()
        };
      });
    }
  } catch {
    // Fall through to delimited-line parsing.
  }
  return rawRows
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const delimiter = line.includes("\t") ? "\t" : line.includes("|") ? "|" : ",";
      const parts = line.split(delimiter);
      const [sourceDocumentId = "", mortgageAmount = "", normalizedValue = "", sourcePage = "", ...excerptParts] = parts;
      return {
        sourceDocumentId: sourceDocumentId.trim(),
        mortgageAmount: mortgageAmount.trim(),
        normalizedValue: normalizedValue.trim() || mortgageAmount.trim(),
        sourcePage: sourcePage.trim(),
        evidenceExcerpt: excerptParts.join(delimiter).trim()
      };
    });
}

